import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import { Button, Box, Typography } from '@mui/material';
import { getCustomerDataAction, getCustomerListAction, getNotesListDataAction, getQuoteListDataAction, getTaskListDataAction } from '../../../redux/actions/customerSearchAction';
import CreateCustomer from './CreateCustomer';
import { setNoteFlag } from '../../../redux/actions/policyActions';
import { userPrefrenceAction, getUserPrefrenceAction } from "../../../redux/actions/dashboardAction";
import DatePickerField from '../../../common/DatePickerDashboard';

const normalize = (s = "") => s.toLowerCase().trim().replace(/\s+/g, "_");

const searchOptions = [
  { label: 'Phone', type: 'number' },
  { label: 'Name', type: 'text' },
  { label: 'Date of Birth', type: 'date' },
  { label: 'Policy Number', type: 'number' },
  { label: 'Source', type: 'text' },
  { label: 'Address', type: 'text' },
  { label: 'Driver Licence', type: 'text' },
];

const DashboardGlobalSearch = ({
  setSelectedRow,
  setTabValue,
  setSelectedCustomer,
  clientCustomerId,
  customerPage,
  customerRowsPerPage,
  setSearchPerformed,
  setShowCustomerData,
  setExpandedView,
  policyRowsPerPage,
  setSearchData,
  policyPage,
  setNewCustomer,
  setPolicyPage,
  rowsPerPage,
  onSearch,
}) => {
  const dispatch = useDispatch();
  const initialDate = dayjs().startOf('month').format('MM-DD-YYYY');
  const [fromDate, setFromDate] = useState(initialDate);
  const [selectedType, setSelectedType] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [typeError, setTypeError] = useState(false);
  const [typeHelperText, setTypeHelperText] = useState('');
  const [keywordError, setKeywordError] = useState(false);
  const [keywordHelperText, setKeywordHelperText] = useState('');
  const [dobValue, setDobValue] = useState('');
  const [dobTouched, setDobTouched] = useState({});
  const [dobErrors, setDobErrors] = useState({});
  const [open, setOpen] = useState(false);

  const location = useLocation();
  const openCreateCustomer = location?.state?.openCreateCustomer || false;
  const get_search_type = useSelector((state) => state?.dashboardData?.show_data || null);
  const navigate = useNavigate();

  const customerDetails = useSelector(state => state.customerDetails.selectedCustomer);

  useEffect(() => {
    if (openCreateCustomer) {
      setOpen(true);
      const clearState = () => {
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, document.title);
        }
      };
      const timer = setTimeout(() => {
        clearState();
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      setOpen(false);
    }
  }, [openCreateCustomer, location.key]);

 const lastSavedTypeRef = useRef(null);

  useEffect(() => {
    dispatch(getUserPrefrenceAction("customer_explorer_last_option"));
  }, [dispatch]);

  useEffect(() => {
    if (get_search_type?.key === "customer_explorer_last_option") {
      const saved = String(get_search_type.value || "");
      lastSavedTypeRef.current = saved;
      sessionStorage.setItem("cx_last_type", saved);

      const found = searchOptions.find(
        (opt) => normalize(opt.label) === saved
      );
      if (found) setSelectedType(found);
    }
  }, [get_search_type]);

  const handleDobChange = (fieldName, value) => {
    setDobValue(value);
  };

  const handleDobBlur = (fieldName, error) => {
    setDobTouched((prev) => ({ ...prev, [fieldName]: true }));
    setDobErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length > 10) return cleaned.slice(0, 10);

    let formatted = cleaned;
    if (cleaned.length > 3) {
      formatted = `${cleaned.substring(0, 3)}-${cleaned.substring(3)}`;
    }
    if (cleaned.length > 6) {
      formatted = `${formatted.substring(0, 7)}-${formatted.substring(7)}`;
    }
    return formatted;
  };

  const handleOpenCreate = () => {
    setOpen(true);
    setTabValue(0);
  };

  const handleCloseCreate = () => {
    setOpen(false);
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, document.title);
    }
  };

  const validateKeyword = (value, type) => {
    if (!value.trim()) {
      return `Please enter ${type.toLowerCase()}`;
    }

    switch (type) {
      case 'Phone':
        if (value.replace(/\D/g, '').length !== 10) {
          return 'Phone number must be exactly 10 digits';
        }
        break;
      case 'Name':
        if (!/^[a-zA-Z ]{3,}$/.test(value)) {
          return 'Enter minimum 3 charecter (letters and spaces only)';
        }
        break;
      case 'Policy Number':
        if (value.length < 3 || value.length > 50) {
          return 'Policy number must be between 3-50 characters';
        }
        break;
     case 'Driver Licence':
        if (value.length < 4) {
          return 'Enter minimum 4 letters or numbers, no spaces.';
        }
        if (value.length > 25) {
          return 'Enter below 25 letters or numbers, no spaces.';
        }
        if (!/^[A-Za-z0-9]+$/.test(value)) {
          return 'Only letters or numbers allowed, no spaces.';
        }
        break;
      default:
        return '';
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    if (selectedType && selectedType.label === 'Phone') {
      const formattedValue = formatPhoneNumber(value);
      setSearchValue(formattedValue);
      setKeywordError(false);
      setKeywordHelperText('');
    } else {
      setSearchValue(value);
      setKeywordError(false);
      setKeywordHelperText('');
    }
  };

  const handleSearch = () => {
    setTabValue(0);
    setNewCustomer(null);
    dispatch({ type: "CLEAR_GET_CLIENT_SUCCESS" });
    dispatch({ type: "CLEAR_GET_CUSTOMER_DETAILS_SUCCESS" });    
    navigate(location.pathname, { replace: true});
    dispatch(setNoteFlag(false));

    if (!selectedType) {
      setTypeError(true);
      setTypeHelperText("Please select a search type");
      return;
    }

    const current = normalize(selectedType.label);
    const saved = String(get_search_type?.value || "") ||
      String(lastSavedTypeRef.current || "") ||
      String(sessionStorage.getItem("cx_last_type") || "");

    if (current && current !== saved) {
      dispatch(
        userPrefrenceAction({
          key: "customer_explorer_last_option",
          value: current,
        })
      );
      lastSavedTypeRef.current = current;
      sessionStorage.setItem("cx_last_type", current);
    }

    if (selectedType.label === 'Date of Birth') {
      if (!dobValue) {
        setDobErrors({ dob: 'Please enter date of birth' });
        setDobTouched({ dob: true });
        return;
      }
    } else {
      const errorMsg = validateKeyword(searchValue, selectedType.label);
      if (errorMsg) {
        setKeywordError(true);
        setKeywordHelperText(errorMsg);
        return;
      }
    }

    const mapping = {
      Phone: "phoneNumber",
      Name: "name",
      "Date of Birth": "dateOfBirth",
      "Policy Number": "policyNumber",
      Source: "source",
      Address: "address",
      "Driver Licence": "driversLicenseNumber",
    };

    const searchParams = {
      name: null,
      policyNumber: null,
      address: null,
      source: null,
      driversLicenseNumber: null,
      phoneNumber: null,
      dateOfBirth: null,
    };

    const key = mapping[selectedType.label];
    if (key) {
      if (selectedType.label === "Date of Birth") {
        searchParams[key] = dobValue;
      } else if (selectedType.label === "Phone") {
        searchParams[key] = searchValue.replace(/\D/g, "");
      } else {
        searchParams[key] = searchValue;
      }
    }
    setPolicyPage(0);
    
    if (onSearch) {
      onSearch(searchParams);
    } else {
    dispatch(getCustomerListAction(searchParams, 0, rowsPerPage));
    }
    
    setSearchPerformed(true);
    setSearchData(searchParams);
    setShowCustomerData(false);
    setExpandedView(false);
  };

  useEffect(() => {
    if (
      customerDetails &&
      customerDetails.id &&
      customerDetails.id === clientCustomerId
    ) {
      setSearchPerformed(true);
      setSelectedRow(customerDetails.id);
      setSelectedCustomer(customerDetails.id);
      setShowCustomerData(true);
      setExpandedView(true);

      dispatch(getCustomerDataAction(customerDetails.id, policyPage, policyRowsPerPage));
      dispatch(getQuoteListDataAction(customerDetails.id, customerPage, customerRowsPerPage));
      dispatch(getNotesListDataAction(customerDetails.id, customerPage, customerRowsPerPage));
      dispatch(getTaskListDataAction(customerDetails.id, customerPage, customerRowsPerPage));
    }
  }, [
    customerDetails,
    clientCustomerId,
    dispatch,
    policyPage,
    policyRowsPerPage,
    customerPage,
    customerRowsPerPage,
  ]);

  const handleReset = () => {
    setSelectedType(null);
    setSearchValue('');
    setFromDate(initialDate);
    setDobValue('');
    setDobTouched({});
    setDobErrors({});
    setTypeError(false);
    setTypeHelperText('');
    setKeywordError(false);
    setKeywordHelperText('');
    setSearchPerformed(false);
    setShowCustomerData(false);
    setNewCustomer(null);
    dispatch({ type: 'CLEAR_GET_CUSTOMER_DETAILS_SUCCESS' });
    dispatch({ type: 'CLEAR_GET_CLIENT_SUCCESS' });
    navigate(location.pathname, { replace: true });
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{justifyContent: 'space-between'}}>
      <Box sx={{ marginTop: '20px', display: 'flex' }}>
        <Box sx={{ minWidth: 350, position: 'relative', minHeight: 80 }}>
          <label>Select Search Type</label>
          <Autocomplete
            sx={{ width: '100%', background: '#FFFFFF' }}
            disablePortal
            options={searchOptions}
            value={selectedType}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            onChange={(event, newValue) => {
              setSelectedType(newValue);
              setTypeError(false);
              setTypeHelperText('');
              setSearchValue('');
              setDobValue('');
              setDobTouched({});
              setDobErrors({});
              setKeywordError(false);
              setKeywordHelperText('');
            }}
            renderInput={(params) => (
              <TextField {...params} error={typeError} />
            )}
          />
          {typeError && (
            <Typography sx={{ color: '#e53935', fontSize: '12px', position: 'absolute', bottom: -18 }}>
              {typeHelperText}
            </Typography>
          )}
        </Box>

        {selectedType && selectedType.label !== 'Date of Birth' && (
          <Box sx={{ minWidth: 300, position: 'relative', minHeight: 80, marginLeft: '20px' }}>
            <label>{`Enter the ${selectedType.label}`}</label>
            <TextField
              sx={{ width: '100%', backgroundColor: '#fff' }}
              variant="outlined"
              value={searchValue}
              onChange={handleInputChange}
              error={keywordError}
              inputProps={{
                maxLength: selectedType.label === 'Phone' ? 12 : selectedType.label === 'Policy Number' ? 50 : undefined,
              }}
            />
            {keywordError && (
              <Typography sx={{ color: '#e53935', fontSize: '12px', position: 'absolute', bottom: -18 }}>
                {keywordHelperText}
              </Typography>
            )}
          </Box>
        )}

        {selectedType && selectedType.label === 'Date of Birth' && (
          <Box sx={{ minWidth: 250, minHeight: 80, marginLeft: '20px', position: 'relative' }}>
            <label>Date of Birth</label>
            <DatePickerField
              fieldName="dob"
              formData={{ dob: dobValue }}
              handleDateChange={handleDobChange}
              handleBlur={handleDobBlur}
              touchedFields={dobTouched}
              fieldErrors={dobErrors}
            />
          </Box>
        )}

        <Box sx={{ marginLeft: '20px' }}>
          <Button variant='contained' size='large' sx={{ marginRight: 1, marginTop: 3,}} onClick={handleSearch}>
            Search
          </Button>
          <Button variant='contained' size='large' sx={{ marginLeft: 1, marginTop: 3, }} onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Box>

      <Box sx={{ marginTop: '22px', width: '20%', display: 'flex', justifyContent: 'end' }}>
        <Button variant='contained' size='large' sx={{ marginTop: 3,}} onClick={handleOpenCreate}>
          Create customer
        </Button>
      </Box>

      <CreateCustomer
        open={open}
        setOpen={setOpen}
        handleClose={handleCloseCreate}
        setNewCustomer={setNewCustomer}
        setSearchPerformed={setSearchPerformed}
        setShowCustomerData={setShowCustomerData}
        setExpandedView={setExpandedView}
        setTabValue={setTabValue}
      />
    </Stack>
  );
};

export default DashboardGlobalSearch;